import React, { useState } from 'react';
import Layout from './Layout';
import { motion } from 'framer-motion';
import { FaCopy } from 'react-icons/fa';

const AArticleAutoDataConsolidation = () => {
  const [copiedStates, setCopiedStates] = useState({});

  const sections = [
    {
      id: 1,
      title: "Automating Financial Data Consolidation",
      text: `In financial reporting, we often need to combine data from many Excel files into one report. Doing this by hand is slow and risky - one small mistake can cause big problems. Let's see how Python can help us do this faster and without errors.`,
      explanation: "Understanding why we need automated data consolidation"
    },
    {
      id: 2,
      title: "Setting Up Your Python Environment",
      text: `First, we'll need below Python libraries to handle Excel files efficiently:`,
      code: `import pandas as pd
import os
import glob

# Check current working directory
print("Working directory:", os.getcwd())
print("Available Python tools:", pd.__version__)`,
      output: `Working directory: C:/Users/Finance/Reports
Available Python tools: 1.5.3`,
      explanation: "Getting your Python workspace ready"
    },
    {
      id: 3,
      title: "Finding Your Excel Files",
      text: `Let's say you have monthly reports from different regions in separate Excel files. Here's how to find them all:`,
      code: `# Look for all Excel files in the Reports folder
excel_files = glob.glob('Monthly Reports/*.xlsx')

# Show what we found
print("Found these Excel files:")
for file in excel_files:
    print(f"- {file}")`,
      output: `Found these Excel files:
- Monthly Reports/North_Region_Jan.xlsx
- Monthly Reports/South_Region_Jan.xlsx
- Monthly Reports/East_Region_Jan.xlsx
- Monthly Reports/West_Region_Jan.xlsx`,
      explanation: "Finding all your Excel files automatically"
    },
    {
      id: 4,
      title: "Combining the Data",
      text: `Now let us combine all these reports into one dataset:`,
      code: `# Create an empty list to store all data
all_data = []

# Read each Excel file and add it to our list
for file in excel_files:
    # Read the Excel file
    df = pd.read_excel(file)
    # Add a column to show which file it came from
    df['Source'] = file
    # Add to our list
    all_data.append(df)

# Combine all data into one
combined_data = pd.concat(all_data, ignore_index=True)

print("Combined data shape:", combined_data.shape)
print("\nFirst few rows:")
print(combined_data.head())`,
      output: `Combined data shape: (1000, 6)

First few rows:
   Revenue  Expenses  Profit Region  Month    Source
0   50000    30000   20000  North    Jan     North_Region_Jan.xlsx
1   45000    28000   17000  North    Jan     North_Region_Jan.xlsx
2   55000    32000   23000  South    Jan     South_Region_Jan.xlsx`,
      explanation: "How to merge multiple Excel files into one dataset"
    },
    {
      id: 5,
      title: "Creating Summary Reports",
      text: `With all our data in one place, we can create useful summaries:`,
      code: `# Calculate totals by region
region_summary = combined_data.groupby('Region').agg({
    'Revenue': 'sum',
    'Expenses': 'sum',
    'Profit': 'sum'
}).round(2)

print("Regional Performance Summary:")
print(region_summary)

# Save to Excel
region_summary.to_excel('Regional_Summary.xlsx')
print("\nSaved summary to Regional_Summary.xlsx")`,
      output: `Regional Performance Summary:
        Revenue  Expenses   Profit
Region
North   150000   90000     60000
South   165000   98000     67000
East    145000   87000     58000
West    155000   93000     62000

Saved summary to Regional_Summary.xlsx`,
      explanation: "Creating the desired financial summaries from the combined data for quick reporting purposes"
    },
    {
      id: 6,
      title: "Making it Easy to Use",
      text: `Let's wrap everything in a function so it's easy to use again:`,
      code: `def consolidate_reports(folder_path, output_file):
    """
    Consolidate all Excel files in a folder and create a summary
    
    folder_path: Where to find the Excel files
    output_file: Where to save the summary
    """
    # Find Excel files
    files = glob.glob(f'{folder_path}/*.xlsx')
    
    # Combine data
    all_data = []
    for file in files:
        df = pd.read_excel(file)
        df['Source'] = file
        all_data.append(df)
    
    combined = pd.concat(all_data, ignore_index=True)
    
    # Create summary
    summary = combined.groupby('Region').agg({
        'Revenue': 'sum',
        'Expenses': 'sum',
        'Profit': 'sum'
    }).round(2)
    
    # Save results
    summary.to_excel(output_file)
    return summary

# Use the function
result = consolidate_reports('Monthly Reports', 'Monthly_Summary.xlsx')
print("Done! Check Monthly_Summary.xlsx for results")`,
      explanation: "Making the consolidation process reusable and easy"
    }
  ];

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedStates({ ...copiedStates, [id]: true });
    setTimeout(() => {
      setCopiedStates(prev => ({ ...prev, [id]: false }));
    }, 2000);
  };

  // Update the scrollToSection function to account for header height
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 100; // Adjust this value based on your header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  // Add this function at the top of your component, after the sections array
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return (
    <div>
      <Layout />
      <div className="w-full min-h-screen bg-gradient-to-b from-white to-white text-black pt-20">
        <div className="max-w-screen-lg mx-auto p-4">
          <div className="mb-12 flex justify-center">
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm max-w-[600px] w-full">
              <h2 className="text-2xl font-bold mb-4 text-center">Table of Contents</h2>
              <div className="space-y-2">
                {sections.map(section => section.title && (
                  <div 
                    key={section.id} 
                    className="cursor-pointer hover:scale-110 transition-colors text-center"
                    
                    onClick={() => scrollToSection(`section-${section.id}`)}
                  >
                    {section.id}. {section.title}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-16">
            {sections.map(({ id, title, text, code, explanation }) => (
              <motion.div 
                key={id}
                id={`section-${id}`}  // Add this line
                initial={{ opacity: 1, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="flex flex-col gap-6"
              >
                <h2 className="text-3xl font-bold">{title}</h2>
                <div className="text-lg whitespace-pre-wrap">{text}</div>
                
                {code && (
                  <div className="relative">
                    <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto">
                      <code className="text-sm">{code}</code>
                    </pre>
                    <button
                      onClick={() => copyToClipboard(code, id)}
                      className="absolute top-2 right-2 p-2 bg-white rounded-md hover:bg-gray-200"
                    >
                      <FaCopy />
                      {copiedStates[id] && (
                        <span className="absolute -right-16 top-2 text-green-600">
                          Copied!
                        </span>
                      )}
                    </button>
                  </div>
                )}
                
                {explanation && (
                  <p className="text-gray-600 italic">{explanation}</p>
                )}
              </motion.div>
            ))}
          </div>
          {/* Back to Top Button */}
          <div className="flex justify-center mt-12 mb-8">
            <button
              onClick={scrollToTop}
              className=" text-white font-semibold py-2 px-4 rounded-lg shadow transition-colors duration-300 hover:scale-110" 
              style={{ background: 'rgb(1, 50, 32)' } }>
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AArticleAutoDataConsolidation;